import { compact, intersection, isEmpty, map, set, sortBy, toLower } from 'lodash/fp';
import { H2, SegmentationBar, TypeWrapper } from '@ahmdigital/ui';
import { PRODUCT_TYPE } from '@ahmdigital/constants';
import { useDispatch, useSelector } from 'react-redux';
import React, { useCallback } from 'react';
import styled from 'styled-components';

import constants from '../../../../ahm-constants';
import CustomerActions from '../../../../actions/customer';
import data from '../../data';
import getFrequencyOptions from '../../../../utils/get-frequency-options';
import getSaleableCombinedProductIds from '../../../../utils/get-saleable-combined-product-ids';
import getSaleableProductIds from '../../../../utils/get-saleable-product-ids';
import getScaleOptions from '../../../../utils/get-scale-options';
import PricingDrawer from '../../../../components/pricing-drawer';
import ProductListingOverlayActions from '../../../../actions/product-listing-overlay';
import ProductsSection from '../../../../components/sections/products-section';
import selectCustomer from '../../../../selectors/select-customer';
import selectProducts from '../../../../selectors/select-products';

const StyledSegmentationWrapper = styled.div`
  margin: auto;
  max-width: 66rem;
`;

const FIND_COVER_SECTION_KEY = 'find-cover';

export const PRODUCT_TYPE_OPTIONS = [
  {
    text: 'hospital and extras',
    value: PRODUCT_TYPE.COMBINED,
  },
  {
    text: `${PRODUCT_TYPE.HOSPITAL} cover`,
    value: PRODUCT_TYPE.HOSPITAL,
  },
  {
    text: `${PRODUCT_TYPE.EXTRAS} cover`,
    value: PRODUCT_TYPE.EXTRAS,
  },
];

// @ts-expect-error - Automatic, Please fix when editing this file
const getProductSectionProps = ({ productType, products, scale }) => {
  const sectionData = data?.sections?.[FIND_COVER_SECTION_KEY];
  const eligibleProductIds = sectionData?.productOptions?.[productType]?.[scale];
  const ctaLink = sectionData?.ctaLinks?.[productType];
  const ctaText = sectionData?.ctaText?.[productType];

  let mappedProducts;
  if (productType === PRODUCT_TYPE.COMBINED) {
    const saleableEligibleCombinedProductIds = intersection(eligibleProductIds, getSaleableCombinedProductIds());
    mappedProducts = map((eligibleProductId) => {
      const eligibleProduct = products?.[eligibleProductId];
      return set('content.longForm', sectionData?.combinedProductBlurbs?.[eligibleProductId], eligibleProduct);
    }, saleableEligibleCombinedProductIds);
  } else {
    const saleableEligibleProductIds = intersection(eligibleProductIds, getSaleableProductIds());
    mappedProducts = map((eligibleProductId) => products?.[eligibleProductId], saleableEligibleProductIds);
  }

  return {
    compareButtonText: ctaText,
    compareButtonToLink: ctaLink,
    products: compact(sortBy('rank', mappedProducts)),
  };
};

const FindCoverSection = () => {
  const dispatch = useDispatch();
  const onChangeFrequency = useCallback(
    (frequency) => {
      dispatch(CustomerActions.changeFrequency(frequency));
    },
    [dispatch],
  );
  const onChangeProductType = useCallback(
    (productType) => {
      dispatch(CustomerActions.changeProductType(productType));
    },
    [dispatch],
  );
  const onChangeScale = useCallback(
    (scale) => {
      dispatch(CustomerActions.changeScale(scale));
    },
    [dispatch],
  );
  const onChangeState = useCallback(
    (state) => {
      dispatch(CustomerActions.changeState(state));
    },
    [dispatch],
  );
  const onEdit = useCallback(() => {
    dispatch(ProductListingOverlayActions.productListingOverlayShowing());
  }, [dispatch]);
  const onRead = useCallback(() => {
    dispatch(ProductListingOverlayActions.productListingOverlayHidden());
  }, [dispatch]);

  const customer = useSelector(selectCustomer);
  const products = useSelector(selectProducts);
  if (isEmpty(products)) {
    return null;
  }

  const { frequency, scale, state } = customer;
  // Defaulting productType to Combined if it has not yet been set in redux state.
  // Also, page does not support package products so use Combined instead.
  let productType = customer?.productType ?? constants.PRODUCT_TYPE.COMBINED;
  if (productType === constants.PRODUCT_TYPE.PACKAGE) {
    productType = constants.PRODUCT_TYPE.COMBINED;
  }
  const { title } = data?.sections?.[FIND_COVER_SECTION_KEY] ?? {};
  const segmentationBarProps = {
    changeFrequency: onChangeFrequency,
    changeProductType: onChangeProductType,
    changeScale: onChangeScale,
    changeState: onChangeState,
    frequency,
    frequencyOptions: getFrequencyOptions({ endText: 'ly', format: toLower }),
    onEdit,
    onRead,
    productType,
    productTypeOptions: PRODUCT_TYPE_OPTIONS,
    scale,
    scaleOptions: getScaleOptions({ endText: 'cover', format: toLower }),
    showProductType: true,
    state,
    stateOptions: constants.STATE_OPTIONS,
  };

  return (
    <TypeWrapper variant={['center', 'marginTopSpacer2', 'marginBottomSpacer6']}>
      <H2>{title}</H2>
      <StyledSegmentationWrapper>
        <SegmentationBar {...segmentationBarProps} />
      </StyledSegmentationWrapper>
      <ProductsSection
        {...getProductSectionProps({ productType, products, scale })}
        withDrawer={false}
        withHorizontalScroll
        withNeedsLink
        withVerticalSpacing={false}
      />
      <PricingDrawer isProductWithHospitalServices={productType !== PRODUCT_TYPE.EXTRAS} />
    </TypeWrapper>
  );
};

export default FindCoverSection;
